import {
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle
} from '@invent/wl-ui-kit-next/ui';
import * as React from 'react';
import { useParams } from 'react-router-dom';

import { useAuth } from '@/domains/auth';
import { oversightApi } from '../api/oversight-api';
import type { IPage, IStaffNote } from '../api/oversight-contracts';
import { LoadError, Pager, TableSkeleton, useAdminData } from '../components/admin-page';
import { NoteComposer, NoteList } from '../components/staff-notes';
import { useResponseContext } from './response-view-page';

/**
 * Staff notes — free text and internal. Only a note's author edits or deletes it (SR-222):
 * survey-api refuses anybody else, an admin included, so the list draws the two presses on the
 * viewer's own notes alone, and a note that changed says so ("edited").
 *
 * Two scopes, because `staff_note` hangs off the **participant** rather than off the
 * response: this response's notes, and the participant's whole history — which includes
 * notes written against their other responses and notes attached to no response at all. A
 * note about a firm predates and outlives any one cycle's response, so a response-only view
 * would hide most of what is known about them. The toggle is the point of the screen, not a
 * convenience.
 *
 * A note written here is always attached to this response; the participant view is a read. A
 * note attached to no response is written from the participant's hub, which draws the same
 * composer and list (`components/staff-notes.tsx`).
 */
export function AdminResponseNotesPage() {
  const { responseId = '' } = useParams();
  const { response } = useResponseContext();
  const { user } = useAuth();
  const [scope, setScope] = React.useState<'response' | 'participant'>('response');
  const [page, setPage] = React.useState(1);

  const participantId = response.participant.participantId;
  const { data, error, setError, reload } = useAdminData<IPage<IStaffNote>>(
    (signal) =>
      scope === 'response'
        ? oversightApi.responseNotes(responseId, { page }, signal)
        : oversightApi.participantNotes(participantId, { page }, signal),
    [scope, responseId, participantId, page]
  );

  const write = async (body: string) => {
    setError(null);
    await oversightApi.writeNote(responseId, body);
    // The new note is the newest, and the list is newest first, so it is on page 1. On page 1
    // that is a re-read; anywhere else it is turning to page 1, which reads it — and a re-read
    // here would ask for the page being left, and could land after page 1 did. (The scope
    // stays as it is: the note is in both, this response's and the participant's.)
    if (page === 1) await reload();
    else setPage(1);
  };

  const switchScope = (next: 'response' | 'participant') => {
    setScope(next);
    setPage(1);
  };

  return (
    <>
      {error && <LoadError message={error} />}

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Add a note</CardTitle>
          <CardDescription>
            Internal to staff and attached to this response — only its author can edit or
            delete it.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <NoteComposer
            placeholder="What a colleague opening this response next should know"
            write={write}
            onRefused={setError}
          />
        </CardContent>
      </Card>

      <div className="flex flex-wrap items-center gap-2">
        <Button
          type="button"
          size="sm"
          variant={scope === 'response' ? 'default' : 'outline'}
          onClick={() => switchScope('response')}
        >
          This response
        </Button>
        <Button
          type="button"
          size="sm"
          variant={scope === 'participant' ? 'default' : 'outline'}
          onClick={() => switchScope('participant')}
        >
          All notes on {response.participant.displayName}
        </Button>
      </div>

      {data === null ? (
        <TableSkeleton rows={3} />
      ) : (
        <>
          <NoteList
            notes={data.items}
            responseId={responseId}
            viewer={user?.accountId ?? ''}
            onChanged={reload}
          />

          <Pager page={data.page} pageSize={data.pageSize} total={data.total} onPage={setPage} />
        </>
      )}
    </>
  );
}
